/**
 * Cloudflare Turnstile for the demo's email unlock (SKE-4166, phase 3).
 *
 * A deliberately slim sibling of signup-onboarding's `useTurnstile`. That hook
 * is not importable here (different app, and it reaches into that app's own
 * `api/signup` + `utils/debugLog`), and most of its 400 lines exist for a
 * multi-step signup flow whose widget container mounts several steps after the
 * hook does. This gate is one form in one dialog, so what is kept is the part
 * that actually matters and that the signup hook learned the hard way:
 *
 *   - a **callback ref**, so the widget initialises at the moment the container
 *     enters the DOM (the dialog mounts its content lazily);
 *   - `render()` called directly once `window.turnstile` exists — NOT via
 *     `turnstile.ready()`, which Cloudflare rejects when api.js is loaded
 *     `async` (as it is in index.html), throwing a TurnstileError into React;
 *   - idempotent init + teardown on unbind, so React StrictMode's
 *     mount→cleanup→remount can't leak or double-render a widget;
 *   - `try/catch` around `reset()`/`remove()`, which throw on a widget Turnstile
 *     has already disposed — an exception thrown from a Turnstile callback
 *     bubbles into React and would break the dialog.
 *
 * NOT CONFIGURED = NOT REQUIRED. With no `VITE_CF_TURNSTILE_SITE` (local dev,
 * mock mode) `isEnabled` is false, no widget renders, and the gate submits
 * without a token. The server is the authority on whether that is acceptable:
 * it rejects a tokenless submission in production and allows it only where
 * Turnstile is explicitly disabled.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

interface TurnstileRenderOptions {
  sitekey: string
  theme?: 'light' | 'dark' | 'auto'
  size?: 'normal' | 'compact' | 'flexible'
  'refresh-expired'?: 'auto' | 'manual' | 'never'
  callback?: (token: string) => void
  'error-callback'?: (errorCode?: string) => void
  'expired-callback'?: () => void
  'timeout-callback'?: () => void
}

interface TurnstileApi {
  ready: (callback: () => void) => void
  render: (
    element: HTMLElement,
    options: TurnstileRenderOptions
  ) => string | undefined
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

export interface UseDemoTurnstileResult {
  /** Callback ref for the widget container. */
  turnstileRef: (element: HTMLDivElement | null) => void
  token: string
  /** False when no site key is configured — render no container, send no token. */
  isEnabled: boolean
  /** The widget reported a failure; the visitor should retry. */
  hasErrored: boolean
  /** Clear the token and re-challenge (e.g. after the server rejects it). */
  reset: () => void
}

const getSitekey = (): string =>
  import.meta.env.VITE_CF_TURNSTILE_SITE?.trim() ?? ''

export const useDemoTurnstile = (): UseDemoTurnstileResult => {
  const sitekey = getSitekey()
  const isEnabled = sitekey.length > 0

  const [token, setToken] = useState('')
  const [hasErrored, setHasErrored] = useState(false)

  const elementRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)

  const initialise = useCallback(() => {
    if (widgetIdRef.current) {
      return
    }
    const element = elementRef.current
    if (!element || !window.turnstile) {
      return
    }

    element.innerHTML = ''
    widgetIdRef.current =
      window.turnstile.render(element, {
        sitekey,
        theme: 'light',
        size: 'flexible',
        'refresh-expired': 'auto',
        callback: (issued: string) => {
          setToken(issued)
          setHasErrored(false)
        },
        'error-callback': () => {
          setToken('')
          setHasErrored(true)
        },
        'expired-callback': () => {
          setToken('')
        },
        'timeout-callback': () => {
          setToken('')
          setHasErrored(true)
        },
      }) ?? null
  }, [sitekey])

  const safeInit = useCallback(() => {
    if (!window.turnstile || !elementRef.current) return
    if (widgetIdRef.current) return

    // api.js is loaded async (see index.html); once `window.turnstile` exists
    // it is ready to render. Do NOT wrap this in `turnstile.ready()` — Cloudflare
    // throws a TurnstileError when ready() is used with an async/defer-loaded
    // api.js, and that exception bubbles into React and breaks the dialog.
    initialise()
  }, [initialise])

  const turnstileRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (element === elementRef.current) {
        return
      }

      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          // Already disposed by Turnstile itself — nothing to remove.
        }
        widgetIdRef.current = null
      }
      elementRef.current = element

      if (!element) {
        setToken('')
        setHasErrored(false)
        return
      }

      safeInit()
    },
    [safeInit]
  )

  // api.js is loaded async in index.html, so it may become available after the
  // container binds. Poll briefly, then give up rather than spin forever.
  useEffect(() => {
    if (!isEnabled || window.turnstile) {
      if (isEnabled) safeInit()
      return
    }

    const startedAt = Date.now()
    const WAIT_TIMEOUT_MS = 30_000
    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval)
        safeInit()
        return
      }
      if (Date.now() - startedAt > WAIT_TIMEOUT_MS) {
        clearInterval(interval)
        console.warn(
          '[external-review] Turnstile api.js did not load within 30s'
        )
      }
    }, 150)

    return () => clearInterval(interval)
  }, [isEnabled, safeInit])

  const reset = useCallback(() => {
    setToken('')
    setHasErrored(false)
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current)
      } catch {
        // Disposed widget — the next open renders a fresh one.
      }
    }
  }, [])

  return { turnstileRef, token, isEnabled, hasErrored, reset }
}
